<template>
  <el-dialog
    title="选择套餐并配置出行信息"
    :visible.sync="dialogVisible"
    width="1180px"
    top="5vh"
    class="purchase-dialog"
  >
    <div v-if="!packages.length" class="dialog-empty">暂无可用套餐</div>

    <div v-else class="purchase-layout">
      <div class="purchase-packages">
        <div class="purchase-block-title">套餐列表</div>
        <div class="purchase-package-list">
          <div
            v-for="pkg in packages"
            :key="pkg.pkg_no"
            class="purchase-package-card"
            :class="{ active: isActivePackage(pkg) }"
            @click="$emit('select-package', pkg)"
          >
            <div class="purchase-package-head">
              <div class="package-name">{{ pkg.pkg_name }}</div>
              <div class="package-price">¥ {{ pkg.b2c_min_price || 0 }}</div>
            </div>
            <div class="purchase-package-foot">
              <span class="package-refund">{{ getPackageRefundDesc(pkg) || '以套餐规则为准' }}</span>
              <el-button
                size="mini"
                type="primary"
                plain
                @click.stop="$emit('select-package', pkg)"
              >
                {{ isActivePackage(pkg) ? '已选中' : '选择' }}
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <div class="purchase-config">
        <div v-if="currentPackageLoading" class="purchase-loading">
          <i class="el-icon-loading"></i>
          <span>套餐信息加载中...</span>
        </div>

        <div v-else-if="!currentPackageDetail" class="purchase-empty">
          请选择左侧套餐
        </div>

        <template v-else>
          <shop-purchase-selection-summary
            :title="currentPackageDetail.pkg_name || currentPackage.pkg_name"
            :unit="currentItem && currentItem.unit ? currentItem.unit : ''"
            :display-date-text="displayDateText"
            :time-slot-text="selectedOptions.selectedTimeSlot ? getTimeSlotName(selectedOptions.selectedTimeSlot) : ''"
            :total-price-b2-c="totalPriceB2C"
            :min-price="currentPackageDetail.b2c_min_price || 0"
            :show-selected-list="false"
            :total-days="selectedOptions.totalDays"
            :selected-skus="selectedOptions.selectedSKUs"
            :get-sku-final-price-b2-c="getSkuFinalPriceB2C"
          />

          <shop-purchase-spec-selector
            :show-date-picker="showDatePicker"
            :date-picker-value="datePickerValue"
            :date-picker-type="datePickerType"
            :date-picker-options="datePickerOptions"
            :sale-start-date="saleStartDate"
            :sale-end-date="saleEndDate"
            :display-date-text="displayDateText"
            :has-time-slots="hasTimeSlots"
            :time-slots="timeSlots"
            :selected-time-slot="selectedOptions.selectedTimeSlot"
            :available-specs="availableSpecs"
            :last-spec-multi="lastSpecMulti"
            :selected-specs="selectedSpecs"
            :matched-skus="matchedSKUs"
            :get-time-slot-name="getTimeSlotName"
            :get-spec-item-price="getSpecItemPrice"
            :get-spec-item-quantity="getSpecItemQuantity"
            :is-spec-item-available-for-date="isSpecItemAvailableForDate"
            :get-sku-final-price-b2-c="getSkuFinalPriceB2C"
            :is-sku-available-for-date="isSkuAvailableForDate"
            @date-input="$emit('date-input', $event)"
            @select-time-slot="$emit('select-time-slot', $event)"
            @select-spec="forwardSelectSpec"
            @increase-spec-quantity="forwardIncreaseSpecQuantity"
            @decrease-spec-quantity="forwardDecreaseSpecQuantity"
            @increase-sku-quantity="$emit('increase-sku-quantity', $event)"
            @decrease-sku-quantity="$emit('decrease-sku-quantity', $event)"
          />

          <shop-purchase-selection-summary
            :unit="currentItem && currentItem.unit ? currentItem.unit : ''"
            :show-overview="false"
            :total-days="selectedOptions.totalDays"
            :selected-skus="selectedOptions.selectedSKUs"
            :get-sku-final-price-b2-c="getSkuFinalPriceB2C"
          />
        </template>
      </div>
    </div>

    <span slot="footer" class="dialog-footer purchase-footer">
      <div class="purchase-total">
        <span>共 {{ totalQuantity }} 份</span>
        <strong>¥ {{ totalPriceB2C }}</strong>
      </div>
      <div class="purchase-footer-actions">
        <el-button @click="dialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="$emit('confirm')">生成预订信息</el-button>
      </div>
    </span>
  </el-dialog>
</template>

<script>
import ShopPurchaseSelectionSummary from './ShopPurchaseSelectionSummary.vue';
import ShopPurchaseSpecSelector from './ShopPurchaseSpecSelector.vue';

export default {
  name: 'ShopPurchaseDialog',
  components: {
    ShopPurchaseSelectionSummary,
    ShopPurchaseSpecSelector,
  },
  props: {
    visible: Boolean,
    packages: {
      type: Array,
      default() {
        return [];
      },
    },
    currentPkgNo: {
      type: [String, Number],
      default: '',
    },
    currentPackage: {
      type: Object,
      default() {
        return {};
      },
    },
    currentPackageLoading: Boolean,
    currentPackageDetail: {
      type: Object,
      default() {
        return null;
      },
    },
    currentItem: {
      type: Object,
      default() {
        return null;
      },
    },
    selectedOptions: {
      type: Object,
      required: true,
    },
    showDatePicker: Boolean,
    datePickerValue: {
      type: [Array, String],
      default: '',
    },
    datePickerType: {
      type: String,
      default: 'date',
    },
    datePickerOptions: {
      type: Object,
      default() {
        return {};
      },
    },
    saleStartDate: {
      type: String,
      default: '',
    },
    saleEndDate: {
      type: String,
      default: '',
    },
    displayDateText: {
      type: String,
      default: '',
    },
    hasTimeSlots: Boolean,
    timeSlots: {
      type: Array,
      default() {
        return [];
      },
    },
    availableSpecs: {
      type: Array,
      default() {
        return [];
      },
    },
    lastSpecMulti: Boolean,
    selectedSpecs: {
      type: Object,
      default() {
        return {};
      },
    },
    matchedSKUs: {
      type: Array,
      default() {
        return [];
      },
    },
    totalQuantity: {
      type: Number,
      default: 0,
    },
    totalPriceB2C: {
      type: Number,
      default: 0,
    },
    getPackageRefundDesc: {
      type: Function,
      required: true,
    },
    getTimeSlotName: {
      type: Function,
      required: true,
    },
    getSpecItemPrice: {
      type: Function,
      required: true,
    },
    getSpecItemQuantity: {
      type: Function,
      required: true,
    },
    isSpecItemAvailableForDate: {
      type: Function,
      required: true,
    },
    getSkuFinalPriceB2C: {
      type: Function,
      required: true,
    },
    isSkuAvailableForDate: {
      type: Function,
      required: true,
    },
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      },
    },
  },
  methods: {
    isActivePackage(pkg) {
      return String(this.currentPkgNo) === String(pkg.pkg_no);
    },
    forwardSelectSpec(specOid, specItemOid) {
      this.$emit('select-spec', specOid, specItemOid);
    },
    forwardIncreaseSpecQuantity(specOid, specItemOid) {
      this.$emit('increase-spec-quantity', specOid, specItemOid);
    },
    forwardDecreaseSpecQuantity(specOid, specItemOid) {
      this.$emit('decrease-spec-quantity', specOid, specItemOid);
    },
  },
};
</script>

<style scoped>
.dialog-empty,
.purchase-empty,
.purchase-loading {
  padding: 40px 0;
  text-align: center;
  color: #999;
}

.purchase-layout {
  display: flex;
  gap: 20px;
  min-height: 620px;
}

.purchase-packages {
  width: 360px;
  flex-shrink: 0;
}

.purchase-block-title {
  color: #20314a;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 16px;
}

.purchase-package-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 620px;
  overflow: auto;
  padding-right: 6px;
}

.purchase-package-card {
  border: 1px solid #e5edf7;
  border-radius: 12px;
  padding: 16px;
  background: #fff;
  cursor: pointer;
  transition: 0.2s ease;
}

.purchase-package-card:hover,
.purchase-package-card.active {
  border-color: #1c5098;
  box-shadow: 0 10px 20px rgb(28 80 152 / 10%);
}

.purchase-package-head,
.purchase-package-foot,
.purchase-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.purchase-package-foot {
  margin-top: 12px;
  gap: 12px;
}

.package-name {
  color: #1f2d3d;
  font-size: 15px;
  font-weight: bold;
  line-height: 1.6;
}

.package-price {
  color: #1c5098;
  font-size: 20px;
  font-weight: bold;
}

.package-refund {
  color: #9099a8;
  font-size: 12px;
  line-height: 1.6;
}

.purchase-config {
  flex: 1;
  min-width: 0;
}

.purchase-total {
  display: flex;
  align-items: center;
  gap: 14px;
  color: #5b6575;
}

.purchase-total strong {
  color: #1c5098;
  font-size: 28px;
}

.purchase-footer-actions {
  display: flex;
  gap: 10px;
}

@media (max-width: 1260px) {
  .purchase-layout {
    display: block;
  }

  .purchase-packages {
    width: 100%;
  }

  .purchase-config {
    margin-top: 20px;
  }

  .purchase-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/shop/components/ShopPurchaseDialog.vue